import { DataTable } from '@fluentsms/agentnet-web-components';
import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import { CellClickedEvent, ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { CurrencyEditor } from 'features/orderManagement/CurrencyEditor';
import { FileMisc, FileNrop } from 'features/orderManagement/types';
import React, { Dispatch, FC, SetStateAction } from 'react';
import AgentNetDivider from 'ui-kit/components/dividers/AgentNetDivider2';
import { DeleteOutlineIcon } from 'ui-kit/icons/DeleteOutlineIcon';
import AgentNetButton from '../../../ui-kit/components/button/AgentNetButton';
import { makeRandomId } from 'utilities/utilities';
import { AgentNetCheckBox } from 'ui-kit/inputs';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(1),
    },
    marginZero: {
      margin: 0,
    },
    paddingTopZero: {
      paddingTop: theme.spacing(0),
    },
  }),
);

export interface NropAmountsType extends FileNrop {
  [id: string]: any | undefined;
  grossPremiumInput?: string;
  netPremiumInput?: string;
  liabilityAmountInput?: string;
  isDeleted?: boolean;
  isDirty?: boolean;
}

interface FSCTabProps {
  fileId: number;
  stateCode?: string;
  nropAmountsData: NropAmountsType[];
  setNropAmountsData: Dispatch<SetStateAction<NropAmountsType[]>>;
  fileMiscData: FileMisc | null;
  setFileMiscData: Dispatch<SetStateAction<FileMisc | null>>;
  isLoading?: boolean;
}

const FSCTab: FC<FSCTabProps> = ({
  fileId,
  stateCode,
  nropAmountsData,
  setNropAmountsData,
  fileMiscData,
  setFileMiscData,
}) => {
  const classes = useStyles();

  const nropAmountsColumnConfig: ColDef<NropAmountsType>[] = [
    {
      colId: 'grossPremiumInput',
      headerName: 'Gross Premium',
      field: 'grossPremiumInput',
      minWidth: 280,
      maxWidth: 280,
      cellRenderer: (params: ICellRendererParams<NropAmountsType>) => {
        const numericValue = Number(params.value);
        if (isNaN(numericValue) || !params.data) {
          return <div>$0.00</div>;
        }
        const newValue = Number(numericValue.toFixed(2));
        if (params.data.grossPremium !== newValue) {
          params.data.isDirty = true;
        }
        params.data.grossPremium = newValue;
        const twoPlaces = newValue.toFixed(2).toString();
        const formatted = `$${twoPlaces.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        return <>{formatted}</>;
      },
      cellEditor: CurrencyEditor,
      onCellClicked: (params: CellClickedEvent) => {
        params.api.startEditingCell({
          rowIndex: params.rowIndex ?? 0,
          colKey: 'grossPremiumInput',
        });
      },
      type: 'rightAligned',
      editable: true,
      suppressHeaderMenuButton: true,
    },
    {
      colId: 'netPremiumInput',
      headerName: 'Net Premium',
      field: 'netPremiumInput',
      minWidth: 280,
      maxWidth: 280,
      cellRenderer: (params: ICellRendererParams<NropAmountsType>) => {
        const numericValue = Number(params.value);
        if (isNaN(numericValue) || !params.data) {
          return <div>$0.00</div>;
        }
        const newValue = Number(numericValue.toFixed(2));
        if (params.data.netPremium !== newValue) {
          params.data.isDirty = true;
        }
        params.data.netPremium = newValue;
        const twoPlaces = newValue.toFixed(2).toString();
        const formatted = `$${twoPlaces.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        return <>{formatted}</>;
      },
      cellEditor: CurrencyEditor,
      onCellClicked: (params: CellClickedEvent) => {
        params.api.startEditingCell({
          rowIndex: params.rowIndex ?? 0,
          colKey: 'netPremiumInput',
        });
      },
      type: 'rightAligned',
      editable: true,
      suppressHeaderMenuButton: true,
    },
    {
      colId: 'liabilityAmountInput',
      headerName: 'Liability Amount',
      field: 'liabilityAmountInput',
      minWidth: 280,
      maxWidth: 280,
      cellRenderer: (params: ICellRendererParams<NropAmountsType>) => {
        const numericValue = Number(params.value);
        if (isNaN(numericValue) || !params.data) {
          return <div>$0.00</div>;
        }
        const newValue = Number(numericValue.toFixed(2));
        if (params.data.liabilityAmount !== newValue) {
          params.data.isDirty = true;
        }
        params.data.liabilityAmount = newValue;
        const twoPlaces = newValue.toFixed(2).toString();
        const formatted = `$${twoPlaces.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        return <>{formatted}</>;
      },
      cellEditor: CurrencyEditor,
      onCellClicked: (params: CellClickedEvent) => {
        params.api.startEditingCell({
          rowIndex: params.rowIndex ?? 0,
          colKey: 'liabilityAmountInput',
        });
      },
      type: 'rightAligned',
      editable: true,
      suppressHeaderMenuButton: true,
    },
    {
      headerName: '',
      floatingFilterComponentParams: { suppressFilterButton: true },
      suppressMenu: true,
      floatingFilter: false,
      sortable: false,
      width: 56,
      cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
      pinned: 'right',
      cellRenderer: DeleteOutlineIcon,
      onCellClicked: (params) => {
        const { data } = params;
        const foundIndex = nropAmountsData.findIndex((nrop) => nrop.id === data?.id);
        if (foundIndex > -1) {
          if (data?.fileId && data?.fileNropId) {
            nropAmountsData[foundIndex].isDeleted = true;
          } else {
            nropAmountsData.splice(foundIndex, 1);
          }
          setNropAmountsData([...nropAmountsData]);
        }
      },
      suppressHeaderMenuButton: true,
    },
  ];

  return (
    <Grid container spacing={2} className={classes.marginZero}>
      {stateCode === 'NY' && fileMiscData && (
        <Grid item sm={9} className={classes.heading}>
          <AgentNetCheckBox
            label="NY Attestation Missing"
            data-testid="fsc-tab-ny-attestation-checkbox"
            checkHandler={(checked: boolean) => {
              setFileMiscData((prev) => ({
                ...prev,
                fileId: prev?.fileId ?? fileId,
                nyAttestation: checked,
              }));
            }}
            value={fileMiscData.nyAttestation ?? false}
          />
        </Grid>
      )}

      <Grid item sm={9} className={classes.heading}>
        <AgentNetDivider typoVariant="h2" title={'No Record of Policy'} disablePadding />
      </Grid>
      <Grid item sm={1} />
      <Grid item sm={2} className={classes.heading}>
        <AgentNetButton
          color="primary"
          size="small"
          variant="contained"
          onClick={() => {
            const nropAmountData: NropAmountsType = {
              id: makeRandomId(),
              fileId: fileId,
              grossPremium: 0,
              grossPremiumInput: '0',
              netPremium: 0,
              netPremiumInput: '0',
              liabilityAmount: 0,
              liabilityAmountInput: '0',
            };
            setNropAmountsData([...nropAmountsData, nropAmountData]);
          }}
          fullWidth={true}
          plusIcon
        >
          Add Fee
        </AgentNetButton>
      </Grid>
      <Grid item xs={12} className={classes.paddingTopZero}>
        <div style={{ height: 200, width: '100%' }}>
          <DataTable
            rowData={nropAmountsData.filter((nrop) => !nrop.isDeleted)}
            columnDefs={nropAmountsColumnConfig}
            animateRows={false}
            noRowsOverlayComponent="customOverlayNoRows"
            stopEditingWhenCellsLoseFocus={true}
            rowHeight={40}
            suppressRowHoverHighlight={true}
            columnHoverHighlight={false}
            rowStyle={{ background: 'none' }}
          />
        </div>
      </Grid>
    </Grid>
  );
};

export default FSCTab;
